import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  Menu,
  Portal,
  Switch,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Columns3,
  FileText,
  Info,
  LineChart,
  Save,
  Settings2,
  Table2,
} from "lucide-react";
import {
  AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  TABLE_GROUPINGS,
  normalizeRenderConfig,
  updateRenderConfig,
} from "../../store/renderConfig";
import Select from "../averly/Select";

const getSortColumnOptions = (columnDefinitions) => [
  { id: "", name: "No sorting" },
  ...columnDefinitions.map((column) => ({
    id: column.id,
    name: column.label,
  })),
];

const InfoTooltip = ({ description }) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <Icon as={Info} w={4} h={4} color="gray.500" cursor="pointer" />
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content>{description}</Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
);

const SettingSwitchRow = ({
  icon: RowIcon,
  title,
  description,
  checked,
  onChange,
  disabled = false,
}) => (
  <HStack
    alignItems="center"
    justifyContent="space-between"
    gap="16px"
    opacity={disabled ? 0.55 : 1}
    py="10px"
  >
    <HStack gap="9px" minW={0}>
      <RowIcon size={16} />
      <Text fontSize="13px" fontWeight="700">
        {title}
      </Text>
      <InfoTooltip description={description} />
    </HStack>
    <Switch.Root
      size="md"
      checked={checked}
      disabled={disabled}
      onCheckedChange={(event) => onChange(Boolean(event.checked))}
    >
      <Switch.HiddenInput />
      <Switch.Control>
        <Switch.Thumb />
      </Switch.Control>
    </Switch.Root>
  </HStack>
);

const SectionBox = ({ children }) => (
  <Box borderTop="1px solid #e5e7eb" pt="8px">
    {children}
  </Box>
);

const SortControls = ({ config, setSortColumn, setSortDirection, columnDefinitions }) => (
  <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px" pb="6px">
    <Box flex="0 1 340px" maxW="340px" minW="220px" w="100%">
      <Select
        label="Sort By"
        value={config.sort?.column || ""}
        options={getSortColumnOptions(columnDefinitions)}
        onChange={setSortColumn}
      />
    </Box>
    <HStack gap="6px" marginLeft="auto">
      <Button
        size="sm"
        variant={config.sort?.direction === "asc" ? "solid" : "outline"}
        onClick={() => setSortDirection("asc")}
      >
        Asc
      </Button>
      <Button
        size="sm"
        variant={config.sort?.direction === "desc" ? "solid" : "outline"}
        onClick={() => setSortDirection("desc")}
      >
        Desc
      </Button>
    </HStack>
  </HStack>
);

const MaybePortal = ({ children, disabled }) => (disabled ? children : <Portal>{children}</Portal>);

const ColumnSelector = ({
  config,
  toggleColumn,
  columnDefinitions,
  disabled = false,
  portalled = true,
}) => (
  <Menu.Root closeOnSelect={false}>
    <Menu.Trigger asChild>
      <Button
        aria-label="Select columns"
        variant="outline"
        size="sm"
        justifyContent="space-between"
        minW="210px"
        disabled={disabled}
      >
        <HStack gap="8px">
          <Columns3 size={15} />
          Columns ({config.columns.length}/{columnDefinitions.length})
        </HStack>
        <ChevronDown size={15} />
      </Button>
    </Menu.Trigger>
    <MaybePortal disabled={!portalled}>
      <Menu.Positioner>
        <Menu.Content bg="white" borderColor="var(--borderGrey)" minW="230px">
          {columnDefinitions.map((column) => {
            const checked = config.columns.includes(column.id);
            return (
              <Menu.Item
                key={column.id}
                closeOnSelect={false}
                disabled={disabled}
                onClick={(event) => {
                  event.preventDefault();
                  if (disabled) return;
                  toggleColumn(column.id, !checked);
                }}
              >
                <Checkbox.Root checked={checked} pointerEvents="none">
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>{column.label}</Checkbox.Label>
                </Checkbox.Root>
              </Menu.Item>
            );
          })}
        </Menu.Content>
      </Menu.Positioner>
    </MaybePortal>
  </Menu.Root>
);

const ControlsBody = ({
  config,
  isSubscription,
  setChartsEnabled,
  setCompactDensity,
  setSortingEnabled,
  setSortColumn,
  setSortDirection,
  toggleColumn,
  columnDefinitions,
  setUnifiedGrouping,
  supportsTableGrouping,
  supportsCharts,
  exportConfig,
  setCsvExportEnabled,
  onSave,
  isSaving,
  saveLabel,
  columnMenuPortalled,
  tableControlsDisabled,
}) => (
  <VStack alignItems="stretch" gap="14px">
    <Box>
      <Text
        fontSize="11px"
        color="gray.600"
        fontWeight="700"
        letterSpacing="0"
        textTransform="uppercase"
        mb="8px"
      >
        Presentation
      </Text>
      {supportsCharts ? (
        <SettingSwitchRow
          icon={LineChart}
          title="Charts"
          description={
            isSubscription
              ? "Include or remove chart pages in the merged PDF."
              : "Show or hide charts in the report view and PDF download."
          }
          checked={config.charts.enabled}
          onChange={setChartsEnabled}
        />
      ) : null}
    </Box>

    <SectionBox>
      <SettingSwitchRow
        icon={Table2}
        title="Table Layout"
        description="Switch on compact table spacing. Switch off for comfortable spacing. Available in table view."
        checked={config.density === "compact"}
        onChange={setCompactDensity}
        disabled={tableControlsDisabled}
      />
    </SectionBox>

    <SectionBox>
      <SettingSwitchRow
        icon={ArrowUpDown}
        title="Sorting"
        description={
          tableControlsDisabled
            ? "Turn charts off to sort rows in table view."
            : isSubscription
            ? "Apply a saved row order to this report inside merged PDFs."
            : "Apply row sorting to the report view and downloads."
        }
        checked={Boolean(config.sort)}
        onChange={setSortingEnabled}
        disabled={tableControlsDisabled}
      />
      {config.sort && !tableControlsDisabled ? (
        <SortControls
          config={config}
          setSortColumn={setSortColumn}
          setSortDirection={setSortDirection}
          columnDefinitions={columnDefinitions}
        />
      ) : null}
    </SectionBox>

    {supportsTableGrouping ? (
      <SectionBox>
        <SettingSwitchRow
          icon={Table2}
          title="Unified table"
          description="Combine service tables into one branch table. Available when charts are off."
          checked={config.tables.grouping === TABLE_GROUPINGS.UNIFIED}
          disabled={config.charts.enabled || tableControlsDisabled}
          onChange={(enabled) =>
            setUnifiedGrouping(enabled ? TABLE_GROUPINGS.UNIFIED : TABLE_GROUPINGS.GROUPED)
          }
        />
      </SectionBox>
    ) : null}

    {isSubscription ? (
      <SectionBox>
        <SettingSwitchRow
          icon={FileText}
          title="CSV export"
          description="Include this report type as a separate full-data CSV file when the merged subscription export is downloaded. Hidden presentation columns do not remove CSV fields."
          checked={Boolean(exportConfig?.csv)}
          onChange={setCsvExportEnabled}
        />
      </SectionBox>
    ) : null}

    <Box borderTop="1px solid #e5e7eb" pt="14px">
      <HStack justifyContent="space-between" gap="10px" flexWrap="wrap">
        <HStack gap="8px">
          <Columns3 size={16} />
          <Text fontSize="13px" fontWeight="700">
            Columns
          </Text>
        </HStack>
        <ColumnSelector
          config={config}
          toggleColumn={toggleColumn}
          columnDefinitions={columnDefinitions}
          portalled={columnMenuPortalled}
          disabled={tableControlsDisabled}
        />
      </HStack>
    </Box>

    {onSave ? (
      <HStack justifyContent="flex-end">
        <Button size="sm" loading={isSaving} onClick={onSave}>
          <Save size={15} />
          {saveLabel}
        </Button>
      </HStack>
    ) : null}
  </VStack>
);

const ReportPresentationControls = ({
  renderConfig,
  onChange,
  onSave,
  saveLabel = "Save Default",
  isSaving = false,
  variant = "direct",
  exportConfig = { csv: false },
  onExportConfigChange,
  supportsTableGrouping = false,
  supportsCharts = true,
  tableControlsRequireChartsOff = false,
  columnDefinitions = AGENT_SERVICE_TIME_COLUMN_DEFINITIONS,
  defaultSortColumn = "average",
}) => {
  const configOptions = { columnDefinitions };
  const normalizedConfig = normalizeRenderConfig(renderConfig, configOptions);
  const config = supportsCharts
    ? normalizedConfig
    : { ...normalizedConfig, charts: { enabled: false } };
  const isSubscription = variant === "subscription";
  const tableControlsDisabled =
    tableControlsRequireChartsOff && supportsCharts && config.charts.enabled;
  const [expanded, setExpanded] = useState(isSubscription);

  const setConfig = (patch) =>
    onChange(
      updateRenderConfig(
        config,
        supportsCharts ? patch : { ...patch, charts: { enabled: false } },
        configOptions
      )
    );
  const setChartsEnabled = (enabled) =>
    setConfig({
      charts: { enabled },
    });
  const setCompactDensity = (enabled) =>
    setConfig({
      density: enabled ? "compact" : "comfortable",
    });
  const setSortingEnabled = (enabled) =>
    setConfig({
      sort: enabled ? config.sort || { column: defaultSortColumn, direction: "desc" } : null,
    });
  const setSortColumn = (column) =>
    setConfig({
      sort: column ? { column, direction: config.sort?.direction || "desc" } : null,
    });
  const setSortDirection = (direction) =>
    setConfig({
      sort: {
        column: config.sort?.column || defaultSortColumn,
        direction,
      },
    });
  const setUnifiedGrouping = (grouping) =>
    setConfig({
      tables: { grouping },
    });
  const setCsvExportEnabled = (enabled) =>
    onExportConfigChange?.({
      ...exportConfig,
      csv: Boolean(enabled),
    });
  const toggleColumn = (columnId, checked) => {
    const columns = checked
      ? [...config.columns, columnId]
      : config.columns.filter((item) => item !== columnId);
    if (!columns.length) return;
    setConfig({ columns });
  };

  const controls = (
    <ControlsBody
      config={config}
      isSubscription={isSubscription}
      setChartsEnabled={setChartsEnabled}
      setCompactDensity={setCompactDensity}
      setSortingEnabled={setSortingEnabled}
      setSortColumn={setSortColumn}
      setSortDirection={setSortDirection}
      toggleColumn={toggleColumn}
      columnDefinitions={columnDefinitions}
      setUnifiedGrouping={setUnifiedGrouping}
      supportsTableGrouping={supportsTableGrouping}
      supportsCharts={supportsCharts}
      exportConfig={exportConfig}
      setCsvExportEnabled={setCsvExportEnabled}
      onSave={onSave}
      isSaving={isSaving}
      saveLabel={saveLabel}
      columnMenuPortalled={!isSubscription}
      tableControlsDisabled={tableControlsDisabled}
    />
  );
  const toggleExpanded = () => setExpanded((value) => !value);
  const handleToggleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleExpanded();
    }
  };

  if (!isSubscription) {
    return (
      <Box borderTop="1px solid #e5e7eb" borderBottom="1px solid #e5e7eb" py="10px" my="16px">
        <HStack justifyContent="space-between" gap="12px" flexWrap="wrap">
          <HStack
            gap="8px"
            role="button"
            tabIndex={0}
            cursor="pointer"
            onClick={toggleExpanded}
            onKeyDown={handleToggleKeyDown}
            aria-expanded={expanded}
            aria-label={expanded ? "Hide presentation settings" : "Show presentation settings"}
            data-testid="presentation-settings-toggle"
          >
            <Settings2 size={16} />
            <Text fontSize="13px" fontWeight="700">
              Presentation settings
            </Text>
          </HStack>
          <Button size="sm" variant="ghost" onClick={toggleExpanded}>
            {expanded ? "Hide" : "Show"}
            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </Button>
        </HStack>
        {expanded ? <Box mt="12px">{controls}</Box> : null}
      </Box>
    );
  }

  return (
    <Box borderTop="1px solid #e5e7eb" borderBottom="1px solid #e5e7eb" py="14px" my="16px">
      {controls}
    </Box>
  );
};

export default ReportPresentationControls;
